import qs from "qs";

import { ApiService } from "services/Api.service";
import { Site, SiteDTO } from "models/site/Site";
import {
  Application,
  ApplicationDTO,
  ApplicationType
} from "models/application/Application";
import { ImportApplicationFormValues } from "components/application/import/Import";
import { Deployment, DeploymentDTO } from "models/application/Deployment";
import { AddUserFormValues } from "components/application/add-user/AddUser";
import { Query, QueryResult } from "components/common/table/types";
import { tableConstructQueryParams } from "utils/query/tableConstructQueryParams";
import { AddApplicationSubscriptionFormValues } from "../../components/application/add-subscription/AddSubscription";

export interface SiteServiceInterface {
  init(): void;
  list: Site[];
  fetchApplication({
    site,
    application
  }: {
    site?: Site;
    application: Application;
  }): Promise<Application>;
  archiveApplication({
    site,
    application
  }: {
    site?: Site;
    application: Application;
  }): Promise<any>;
  applicationList({
    site,
    query,
    archived
  }: {
    site?: Site;
    query: Query;
    archived?: boolean;
  }): Promise<QueryResult<Application>>;
  createApplication({
    site,
    values
  }: {
    site?: Site;
    values: CreateApplicationDTO;
  }): Promise<void>;
  defaultSite(): Site;
}

export type CreateApplicationDTO = {
  name: string;
  environment: string;
  mainDomainName: string | null;
  note: string | null;
  accountManagerNote: string | null;
  redirectDomainNames: string[];
  packages: string[];
  rewardCafeteriaEnabled: boolean;
  workflowsEnabled: boolean;
  oldAdminEnabled: boolean;
  applicationType: ApplicationType;
  billingEmail: string;
};

export type EditApplicationDTO = {
  environment: string;
  mainDomainName: string | null;
  note: string | null;
  accountManagerNote: string | null;
  redirectDomainNames: string[];
  packages: string[];
  rewardCafeteriaEnabled: boolean;
  workflowsEnabled: boolean;
  oldAdminEnabled: boolean;
  applicationType: ApplicationType;
  billingEmail: string;
};

export class SiteService implements SiteServiceInterface {
  private _apiService: ApiService;
  private _list: Site[] = [];

  constructor(apiService: ApiService) {
    this._apiService = apiService;
  }

  public async init() {
    const { list } = await this._apiService.get("/overmind/site");

    this._list = list.map((site: SiteDTO) => new Site(site));
  }

  public get list(): Site[] {
    return this._list;
  }

  public async fetchApplication({
    site = this.defaultSite(),
    application
  }: {
    site?: Site;
    application: Application;
  }): Promise<Application> {
    const response = await this._apiService.get(
      `/overmind/site/${site.name}/application/${application.name}`
    );

    return Application.create(response);
  }

  public async archiveApplication({
    site = this.defaultSite(),
    application
  }: {
    site?: Site;
    application: Application;
  }): Promise<any> {
    return await this._apiService.post(
      `/overmind/site/${site.name}/deployment/${application.name}/archive`
    );
  }

  public async applicationList({
    site = this.defaultSite(),
    query,
    archived = false
  }: {
    site?: Site;
    query: Query;
    archived?: boolean;
  }): Promise<QueryResult<Application>> {
    const queryParams = {
      ...tableConstructQueryParams(query)
    };

    const { count, list } = await this._apiService.get(
      `/overmind/site/${site.name}/application`,
      {
        searchFields: [],
        archived,
        ...queryParams,
        filters: {
          archived: Number(archived)
        }
      },
      {
        paramsSerializer: (params: any) => qs.stringify(params)
      }
    );

    return {
      count,
      list: list.map((application: ApplicationDTO) =>
        Application.create(application)
      )
    };
  }

  public async createApplication({
    site = this.defaultSite(),
    values
  }: {
    site?: Site;
    values: CreateApplicationDTO;
  }) {
    await this._apiService.post(
      `/overmind/site/${site.name}/application`,
      values
    );
    // deploy application:
    await this._apiService.post(
      `/overmind/site/${site.name}/deployment/${values.name}`
    );
  }

  public async editApplication({
    site = this.defaultSite(),
    application,
    values
  }: {
    site?: Site;
    application: Application;
    values: EditApplicationDTO;
  }) {
    await this._apiService.put(
      `/overmind/site/${site.name}/application/${application.name}`,
      values
    );
    // deploy application:
    await this._apiService.post(
      `/overmind/site/${site.name}/deployment/${application.name}`
    );
  }

  public async deployApplication({
    site = this.defaultSite(),
    application
  }: {
    site?: Site;
    application: Application;
  }) {
    await this._apiService.post(
      `/overmind/site/${site.name}/deployment/${application.name}`
    );
  }

  public async eraseApplication({
    site = this.defaultSite(),
    application,
    options = {}
  }: {
    site?: Site;
    application: Application;
    options?: {
      removeFromList?: boolean;
      removeJobsAndDeployments?: boolean;
    };
  }) {
    await this._apiService.post(
      `/overmind/site/${site.name}/deployment/${application.name}/erase`,
      options
    );
  }

  public async addUser({
    site = this.defaultSite(),
    application,
    values
  }: {
    site?: Site;
    application: Application;
    values: AddUserFormValues;
  }) {
    await this._apiService.post(
      `/overmind/site/${site.name}/maintenance/application/${application.name}/add-admin-user`,
      values
    );
  }

  public async addApplicationSubscription({
    site = this.defaultSite(),
    application,
    values
  }: {
    site?: Site;
    application: Application;
    values: AddApplicationSubscriptionFormValues;
  }) {
    await this._apiService.post(
      `/overmind/site/${site.name}/maintenance/application/${application.name}/subscription`,
      {
        ...values,
        startDate: `${values.startDate.getDate()}-${values.startDate.getMonth() +
          1}-${values.startDate.getFullYear()}`,
        endDate: `${values.endDate.getDate()}-${values.endDate.getMonth() +
          1}-${values.endDate.getFullYear()}`
      }
    );
  }

  public async deployments({
    site = this.defaultSite(),
    application
  }: {
    site?: Site;
    application: Application;
  }) {
    return await this._apiService
      .get(`/overmind/site/${site.name}/deployment/${application.name}`)
      .then(({ list }) =>
        list.map((deployment: DeploymentDTO) => new Deployment(deployment))
      );
  }

  public defaultSite() {
    return this._list[0];
  }

  public async importApplicationData({
    application,
    site = this.defaultSite(),
    values
  }: {
    site?: Site;
    application: Application;
    values: ImportApplicationFormValues;
  }): Promise<void> {
    await this._apiService.post(
      `/overmind/site/${site.name}/maintenance/application/${application.name}/import-application`,
      values
    );
  }
}
